// This file is auto-generated. Do not edit manually.

import { z } from "zod";
import {
	PaginationInfoSchema,
	StatusSchema,
	ResponseMessageSchema,
} from "./utilitySchemas";

const isValidPhoneNumber = (value: string) => {
	const normalizedValue = value.replace(/[()\s-]/g, "");
	return /^\+[1-9]\d{7,14}$/.test(normalizedValue);
};

// DOC: Defines the structure of a user role
export const UserRoleSchema = z.object({
	id: z.number(),
	name: z.string(),
	identifier: z.string(), // Optional field for role identifier
});
export type UserRole = z.infer<typeof UserRoleSchema>;

export const UserTableColumnsSchema = z.array(
	z.enum([
		"id",
		"evalionId",
		"updatedAt",
		"type",
		"assignee",
		"property",
		"user",
		"status",
		"documentStatus",
		"invoice",
		"submittedAt",
		"source",
		"requestVariant",
		"aptDescription",
		"description",
		"viewing",
		"accountOwner",
		"viewingStatus",
		"deadlineDate",
		"orderedAt"
	])
);
export type UserTableColumns = z.infer<typeof UserTableColumnsSchema>;

export const RequestFilterPresetSearchParamsSchema = z.record(z.string());
export type RequestFilterPresetSearchParams = z.infer<
	typeof RequestFilterPresetSearchParamsSchema
>;

export const RequestFilterPresetSchema = z.object({
	id: z.number().int().positive(),
	userId: z.number().int().positive(),
	name: z.string(),
	searchParams: RequestFilterPresetSearchParamsSchema,
	createdAt: z.string(),
	updatedAt: z.string(),
});
export type RequestFilterPreset = z.infer<typeof RequestFilterPresetSchema>;

export const CreateRequestFilterPresetSchema = z.object({
	name: z.string().trim().min(1, "Filter name is required"),
	searchParams: RequestFilterPresetSearchParamsSchema,
});
export type CreateRequestFilterPreset = z.infer<
	typeof CreateRequestFilterPresetSchema
>;

export const UpdateRequestFilterPresetSchema = z.object({
	name: z.string().trim().min(1, "Filter name is required").optional(),
	searchParams: RequestFilterPresetSearchParamsSchema.optional(),
});
export type UpdateRequestFilterPreset = z.infer<
	typeof UpdateRequestFilterPresetSchema
>;
// DOC: Defines the structure of a user entity (e.g., Company or Organization)
export const UserEntitySchema = z.object({
	id: z.number(),
	name: z.string(),
	streetName: z.string(),
	streetNumber: z.string(),
	city: z.string(),
	postalCode: z.string(),
	country: z.string(),
	industry: z.string(),
	type: z.string(),
	active: z.boolean(),
	appraiserId: z.string().optional(),
	referralSourceId: z.string().nullable(),
	appraiser: z
		.object({
			id: z.string().uuid(),
			name: z.string(),
			display: z.boolean(),
			identifier: z.string(),
			showUserManagement: z.boolean(),
		})
		.nullable(),
	leadSource: z.enum([
		"GUTACHTEN_ORG",
		"PERSONAL_RECOMMENDATION",
		"DERGUTACHTER_LARS",
		"SACHNET",
		"IMMOCATION",
		"SOCIAL_MEDIA",
		"INTERNET_SEARCH",
		"OTHER",
	]),
	stripePriceListId: z.string().nullable(),
	stripePriceList: z
		.object({
			id: z.string(),
			name: z.string(),
			description: z.string(),
		})
		.nullable(),
	_count: z.object({
		users: z.number(),
	}),
});
export type UserEntity = z.infer<typeof UserEntitySchema>;

// DOC: Defines the complete structure of a user returned from the API
export const UserSchema = z.object({
	id: z.number(),
	evalionId: z.string(),
	firstName: z.string(),
	lastName: z.string(),
	email: z.string().email(),
	phoneNumber: z.string(),
	active: z.boolean(),
	deactivationReason: z.string().nullable().optional(),
	deactivatedByUserId: z.number().nullable().optional(),
	deactivatedByRoleId: z.number().nullable().optional(),
	deactivatedAt: z.date().nullable().optional(),
	reactivatedByUserId: z.number().nullable().optional(),
	reactivatedAt: z.date().nullable().optional(),
	tableColumns: z.string(),
	roleId: z.number(),
	role: UserRoleSchema,
	entity: UserEntitySchema,
	createdAt: z.date(),
	updatedAt: z.date(),
	podAdminId: z.number().nullable(),
	priceListId: z.number().nullable(),
	showPriceList: z.boolean(),
	paymentContact: z.boolean(),
	_count: z.object({
		assignedRequests: z.number(),
		assignedTempRequests: z.number(),
		assignedRequestAccounts: z.number(),
		assignedTempRequestAccounts: z.number(),
		assignedTasks: z.number(),
	}),
});
export type User = z.infer<typeof UserSchema>;

// Create a new schema based on UserSchema with string dates
export const UserResponseSchema = UserSchema.omit({
	createdAt: true,
	updatedAt: true,
	deactivatedAt: true,
	reactivatedAt: true,
}).extend({
	createdAt: z.string(),
	updatedAt: z.string(),
	deactivatedAt: z.string().nullable().optional(),
	reactivatedAt: z.string().nullable().optional(),
});
export type UserResponse = z.infer<typeof UserResponseSchema>;

// DOC: Schema for creating a new user
export const CreateUserSchema = z.object({
	firstName: z.string().min(2),
	lastName: z.string().min(2),
	email: z.string().email(),
	phoneNumber: z.string().refine(isValidPhoneNumber, {
		message: "Invalid phone number",
	}),
	password: z.string().min(8).optional(),
	roleId: z.number().int().positive(),
	entityId: z.number().int().positive(),
	podAdminId: z.number().nullable(),
	priceListId: z.number().nullable(),
	showPriceList: z.boolean(),
	position: z.string().min(3).optional(),
	hubspotContactId: z.string().optional(),
});
export type CreateUser = z.infer<typeof CreateUserSchema>;

// DOC: Schema for updating an existing user
export const UpdateUserSchema = z.object({
	firstName: z.string().min(2).optional(),
	lastName: z.string().min(2).optional(),
	email: z.string().email().optional(),
	phoneNumber: z
		.string()
		.optional()
		.refine((value) => value === undefined || isValidPhoneNumber(value), {
			message: "Invalid phone number",
		}),
	password: z.string().min(8).optional(),
	newPassword: z.string().min(8).optional(),
	podAdminId: z.number().nullable().optional(),
	active: z.boolean().optional(),
	priceListId: z.number().nullable().optional(),
	showPriceList: z.boolean().optional(),
	updateType: z
		.enum(["basic", "password", "register", "general"])
		.optional()
		.default("register"),
	isRegisterLastStep: z.boolean().optional(),
	paymentContact: z.boolean().optional(),
});
export type UpdateUser = z.infer<typeof UpdateUserSchema>;

// DOC: Schema for updating a user's profile
export const UpdateUserProfileSchema = z.object({
	firstName: z.string().min(2, "First name is required"),
	lastName: z.string().min(2, "Last name is required"),
	email: z.string().email("Invalid email address"),
	phoneNumber: z
		.string()
		.min(1, "Phone number is required")
		.refine(isValidPhoneNumber, {
			message: "Invalid phone number",
		}),
});
export type UpdateUserProfile = z.infer<typeof UpdateUserProfileSchema>;

// TODO: Add error messages to all schemas

// DOC: Schema for registering a user
export const RegisterNewUserSchema = z.object({
	firstName: z.string().min(2),
	lastName: z.string().min(2),
	email: z.string().email(),
	phoneNumber: z.string().refine(isValidPhoneNumber, {
		message: "Invalid phone number",
	}),
	password: z.string().min(8),
	roleId: z.number().int().positive(),
	entityId: z.number().int().positive(),
	podAdminId: z.number().nullable(),
	priceListId: z.number().nullable(),
	showPriceList: z.boolean(),
});
export type RegisterNewUser = z.infer<typeof RegisterNewUserSchema>;

// DOC: Schema for updating a user's password
export const UpdateUserPasswordSchema = z.object({
	id: z.number(),
	email: z.string().email(),
	password: z.string().min(8),
});
export type UpdateUserPassword = z.infer<typeof UpdateUserPasswordSchema>;

export const PasswordSchema = z
	.string()
	.refine((val) => val.length >= 8, {
		message: "Password must be at least 8 characters long",
	})
	.refine((val) => /[a-zA-Z]/.test(val), {
		message: "Password must contain at least one letter",
	})
	.refine((val) => /\d/.test(val), {
		message: "Password must contain at least one number",
	});

export const UserChangePasswordSchema = z
	.object({
		password: z.string().min(1, "Current password is required"),
		newPassword: PasswordSchema,
	})
	.refine((data) => data.password !== data.newPassword, {
		message: "New password must be different from the current password",
		path: ["newPassword"],
	});
export type UserChangePassword = z.infer<typeof UserChangePasswordSchema>;

export const UserUpdateBasicInfoSchema = z.object({
	firstName: z.string().min(2).optional(),
	lastName: z.string().min(2).optional(),
	email: z.string().email().optional(),
});
export type UserUpdateBasicInfo = z.infer<typeof UserUpdateBasicInfoSchema>;

// DOC: Schema for checking if a user exists
export const UserCheckSchema = z.object({
	email: z.string().email(),
});
export type UserCheck = z.infer<typeof UserCheckSchema>;

export const UserSortByEnumSchema = z.enum([
	"id",
	"firstName",
	"lastName",
	"email",
	"createdAt",
	"updatedAt",
	"phoneNumber",
	"active",
	"paymentContact",
]);
export type UserSortByEnum = z.infer<typeof UserSortByEnumSchema>;

// DOC: Schema for querying users with various filters and sorting options
export const UserQuerySchema = z.object({
	limit: z.coerce.number().int().positive().optional(),
	offset: z.coerce.number().int().nonnegative().optional(),
	sortBy: UserSortByEnumSchema.optional(),
	sortOrder: z.enum(["asc", "desc"]).optional(),
	roleIds: z.preprocess((val) => {
		if (typeof val === "string") {
			return val.split(",").map((id) => parseInt(id, 10));
		}
		if (Array.isArray(val)) {
			return val.map(Number);
		}
		return undefined;
	}, z.array(z.number()).optional()),
	filter: z
		.object({
			active: z
				.union([
					z.boolean(),
					z.enum(["true", "false"]).transform((val) => val === "true"),
				])
				.optional(),
			entityId: z.coerce.number().int().optional(),
			appraiserId: z.string().optional(),
			paymentContact: z
				.union([
					z.boolean(),
					z.enum(["true", "false"]).transform((val) => val === "true"),
				])
				.optional(),
			search: z.string().optional(),
		})
		.optional(),
});
export type UserQuery = z.infer<typeof UserQuerySchema>;

export const UserFilterQuerySchema = UserQuerySchema.shape.filter;
export type UserFilterQuery = z.infer<typeof UserFilterQuerySchema>;

// DOC: Schema for querying pod users with filters and sorting options
export const PodUsersQuerySchema = z.object({
	limit: z.coerce.number().int().positive().optional(),
	offset: z.coerce.number().int().nonnegative().optional(),
	sortBy: z
		.enum(["id", "firstName", "lastName", "email", "createdAt", "updatedAt"])
		.optional(),
	sortOrder: z.enum(["asc", "desc"]).optional(),
	filter: z
		.object({
			active: z
				.union([
					z.boolean(),
					z.enum(["true", "false"]).transform((val) => val === "true"),
				])
				.optional(),
		})
		.optional(),
});
export type PodUsersQuery = z.infer<typeof PodUsersQuerySchema>;

/**
 * DOC: USER RESPONSE SCHEMAS
 */

export const GetUserResponseSchema = z.object({
	status: StatusSchema,
	message: ResponseMessageSchema,
	data: UserResponseSchema,
});

export const CreateUserResponseSchema = z.object({
	status: StatusSchema,
	message: ResponseMessageSchema,
	data: z.null(),
});

export const GetUsersResponseSchema = z.object({
	status: StatusSchema,
	message: ResponseMessageSchema,
	data: z.object({
		users: z.array(UserResponseSchema),
		paginationInfo: PaginationInfoSchema,
	}),
});

export const UpdateUserResponseSchema = z.object({
	status: StatusSchema,
	message: ResponseMessageSchema,
	data: UpdateUserSchema.nullable(),
});

export const GetPodUsersResponseSchema = z.object({
	status: StatusSchema,
	data: z.object({
		podMembers: z.array(UserResponseSchema),
		paginationInfo: PaginationInfoSchema,
	}),
});

export const CheckUserFieldUniquenessSchema = z.object({
	field: z.enum(["email", "phoneNumber"]),
	value: z.string().min(1, "Value cannot be empty"),
});

export const CheckUserFieldUniquenessResponseSchema = z.object({
	status: StatusSchema,
	data: z.object({
		exists: z.boolean(),
	}),
});

export const UserDeleteResponseSchema = z.object({
	status: StatusSchema,
	message: ResponseMessageSchema,
	data: z.null(),
});

export const GetRequestFilterPresetsResponseSchema = z.object({
	status: StatusSchema,
	message: ResponseMessageSchema.optional(),
	data: z.object({
		presets: z.array(RequestFilterPresetSchema),
	}),
});

export const RequestFilterPresetResponseSchema = z.object({
	status: StatusSchema,
	message: ResponseMessageSchema.optional(),
	data: RequestFilterPresetSchema,
});

export const DeleteRequestFilterPresetResponseSchema = z.object({
	status: StatusSchema,
	message: ResponseMessageSchema.optional(),
	data: z.null(),
});

export const InviteExternalUserResponseSchema = z.object({
	status: StatusSchema,
	message: ResponseMessageSchema,
	data: z.null(),
});
